// The delivery record (docs/plans/phase2-maternity-mch.md §2.7, §4.4). Everything is as
// entered: blood loss is a number in ml with no haemorrhage threshold, weight is grams with
// no low-birth-weight band, and Apgar is the score's own 0–10 scale.

import { BIRTH_ORDER_MAX } from '../vital-events/birth.ts';

export const DELIVERY_MODES = [
  'spontaneous_vaginal',
  'assisted_vaginal',
  'vaginal_breech',
  'caesarean_section',
  'other',
] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

/** TODO(verify-spec) M13: align with the MOH 333 maternity register's coding. */
export const DELIVERY_MODE_LABELS: Readonly<Record<DeliveryMode, string>> = {
  spontaneous_vaginal: 'Spontaneous vaginal',
  assisted_vaginal: 'Assisted vaginal (vacuum or forceps)',
  vaginal_breech: 'Vaginal breech',
  caesarean_section: 'Caesarean section',
  other: 'Other',
};

export const DELIVERY_OUTCOMES = ['live_birth', 'stillbirth'] as const;
export type DeliveryOutcome = (typeof DELIVERY_OUTCOMES)[number];

export const DELIVERY_OUTCOME_LABELS: Readonly<Record<DeliveryOutcome, string>> = {
  live_birth: 'Live birth',
  stillbirth: 'Stillbirth',
};

export const PLACENTA_STATES = ['complete', 'incomplete', 'not_recorded'] as const;
export type PlacentaState = (typeof PLACENTA_STATES)[number];

export const PLACENTA_LABELS: Readonly<Record<PlacentaState, string>> = {
  complete: 'Complete',
  incomplete: 'Incomplete',
  not_recorded: 'Not recorded',
};

export const YES_NO_NOT_RECORDED = ['yes', 'no', 'not_recorded'] as const;
export type YesNoNotRecorded = (typeof YES_NO_NOT_RECORDED)[number];

export const YES_NO_NOT_RECORDED_LABELS: Readonly<Record<YesNoNotRecorded, string>> = {
  yes: 'Yes',
  no: 'No',
  not_recorded: 'Not recorded',
};

/**
 * Born before arrival (plan M24): a delivery or birth time may be before the pregnancy record
 * was opened, back to this many days before the admission. TODO(clinical-verify) M24: a
 * provisional data-entry bound, not a clinical rule.
 */
export const BORN_BEFORE_ARRIVAL_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The earliest delivery or birth time accepted for a stay admitted at this time. */
export function earliestBirthTime(admittedAt: Date): Date {
  return new Date(admittedAt.getTime() - BORN_BEFORE_ARRIVAL_DAYS * DAY_MS);
}

/** Typing-error bounds (plan M7), not clinical limits. */
export const BIRTH_WEIGHT_GRAMS_MAX = 9999;
export const BLOOD_LOSS_ML_MAX = 10_000;
export const APGAR_MAX = 10;

export interface DeliveryBabyInput {
  readonly birthOrder: number;
  readonly bornAt: Date;
  readonly outcome: DeliveryOutcome;
  readonly birthWeightGrams: number | null;
  readonly apgar1: number | null;
  readonly apgar5: number | null;
}

export interface DeliveryInput {
  readonly deliveredAt: Date;
  readonly mode: DeliveryMode;
  readonly hasTheatreCase: boolean;
  readonly placentaDeliveredAt: Date | null;
  readonly bloodLossMl: number | null;
  readonly babies: readonly DeliveryBabyInput[];
  /** When care began: the labour record's opening, or the admission. */
  readonly earliest: Date;
  readonly now: Date;
}

export type DeliveryErrorCode =
  | 'delivery.caesarean_needs_theatre_case'
  | 'delivery.theatre_case_not_caesarean'
  | 'delivery.no_babies'
  | 'delivery.birth_order_invalid'
  | 'delivery.time_invalid'
  | 'delivery.placenta_time_invalid'
  | 'delivery.apgar_invalid'
  | 'delivery.birth_weight_invalid'
  | 'delivery.blood_loss_invalid';

const within = (at: Date, from: Date, to: Date) =>
  at.getTime() >= from.getTime() && at.getTime() <= to.getTime();

const wholeIn = (n: number | null, min: number, max: number) =>
  n === null || (Number.isInteger(n) && n >= min && n <= max);

export function checkDelivery(input: DeliveryInput): DeliveryErrorCode | null {
  if (input.mode === 'caesarean_section' && !input.hasTheatreCase) {
    return 'delivery.caesarean_needs_theatre_case';
  }
  if (input.mode !== 'caesarean_section' && input.hasTheatreCase) {
    return 'delivery.theatre_case_not_caesarean';
  }
  if (input.babies.length === 0) return 'delivery.no_babies';
  const orders = new Set<number>();
  for (const baby of input.babies) {
    if (!wholeIn(baby.birthOrder, 1, BIRTH_ORDER_MAX) || orders.has(baby.birthOrder)) {
      return 'delivery.birth_order_invalid';
    }
    orders.add(baby.birthOrder);
  }
  if (!within(input.deliveredAt, input.earliest, input.now)) return 'delivery.time_invalid';
  if (input.babies.some((b) => !within(b.bornAt, input.earliest, input.now))) {
    return 'delivery.time_invalid';
  }
  const firstBirth = Math.min(...input.babies.map((b) => b.bornAt.getTime()));
  if (
    input.placentaDeliveredAt !== null &&
    (input.placentaDeliveredAt.getTime() < firstBirth ||
      input.placentaDeliveredAt.getTime() > input.now.getTime())
  ) {
    return 'delivery.placenta_time_invalid';
  }
  for (const baby of input.babies) {
    if (baby.outcome === 'stillbirth' && (baby.apgar1 !== null || baby.apgar5 !== null)) {
      return 'delivery.apgar_invalid';
    }
    if (!wholeIn(baby.apgar1, 0, APGAR_MAX) || !wholeIn(baby.apgar5, 0, APGAR_MAX)) {
      return 'delivery.apgar_invalid';
    }
    if (!wholeIn(baby.birthWeightGrams, 1, BIRTH_WEIGHT_GRAMS_MAX)) {
      return 'delivery.birth_weight_invalid';
    }
  }
  if (!wholeIn(input.bloodLossMl, 0, BLOOD_LOSS_ML_MAX)) return 'delivery.blood_loss_invalid';
  return null;
}
